import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfileUpdatedPayload } from '@maios/shared';
import { connectSocket, disconnectSocket, joinProject, leaveProject, SOCKET_EVENTS, getSocket } from '../lib/socket';
import { useAuthStore } from '../store/authStore';
import { getMe } from '../services/authService';
import toast from 'react-hot-toast';

// 앱 전체 소켓 연결 (AppLayout 에서 1회 호출)
export function useSocketConnection() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      connectSocket();
    }
    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated]);
}

// 프로젝트 룸 구독 + 이벤트 핸들링
export function useProjectSocket(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const prevProjectId = useRef<string>();

  useEffect(() => {
    if (!projectId) return;

    // 이전 프로젝트 leave
    if (prevProjectId.current && prevProjectId.current !== projectId) {
      leaveProject(prevProjectId.current);
    }

    joinProject(projectId);
    prevProjectId.current = projectId;

    const socket = getSocket();
    if (!socket) return;

    // 이슈 이벤트 → react-query 캐시 무효화
    const handleIssueCreated = () => {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const handleIssueUpdated = () => {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const handleIssueStatusChanged = () => {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    // REQ-V18-011: 이슈 삭제 이벤트
    const handleIssueDeleted = () => {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const handleTestCaseExecuted = () => {
      void queryClient.invalidateQueries({ queryKey: ['testcases'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const handleDashboardRefresh = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    // CHG-021: TC 검토완료 이벤트
    const handleReviewCompleted = () => {
      void queryClient.invalidateQueries({ queryKey: ['testCases', projectId] });
    };
    const handleBatchReviewCompleted = () => {
      void queryClient.invalidateQueries({ queryKey: ['testCases', projectId] });
    };

    socket.on(SOCKET_EVENTS.ISSUE_CREATED, handleIssueCreated);
    socket.on(SOCKET_EVENTS.ISSUE_UPDATED, handleIssueUpdated);
    socket.on(SOCKET_EVENTS.ISSUE_STATUS_CHANGED, handleIssueStatusChanged);
    socket.on(SOCKET_EVENTS.ISSUE_DELETED, handleIssueDeleted);
    socket.on(SOCKET_EVENTS.TESTCASE_EXECUTED, handleTestCaseExecuted);
    socket.on(SOCKET_EVENTS.TESTCASE_FAILED, handleTestCaseExecuted);
    socket.on(SOCKET_EVENTS.DASHBOARD_REFRESH, handleDashboardRefresh);
    socket.on(SOCKET_EVENTS.TESTCASE_REVIEW_COMPLETED, handleReviewCompleted);
    socket.on(SOCKET_EVENTS.TESTCASE_BATCH_REVIEW_COMPLETED, handleBatchReviewCompleted);

    return () => {
      leaveProject(projectId);
      socket.off(SOCKET_EVENTS.ISSUE_CREATED, handleIssueCreated);
      socket.off(SOCKET_EVENTS.ISSUE_UPDATED, handleIssueUpdated);
      socket.off(SOCKET_EVENTS.ISSUE_STATUS_CHANGED, handleIssueStatusChanged);
      socket.off(SOCKET_EVENTS.ISSUE_DELETED, handleIssueDeleted);
      socket.off(SOCKET_EVENTS.TESTCASE_EXECUTED, handleTestCaseExecuted);
      socket.off(SOCKET_EVENTS.TESTCASE_FAILED, handleTestCaseExecuted);
      socket.off(SOCKET_EVENTS.DASHBOARD_REFRESH, handleDashboardRefresh);
      socket.off(SOCKET_EVENTS.TESTCASE_REVIEW_COMPLETED, handleReviewCompleted);
      socket.off(SOCKET_EVENTS.TESTCASE_BATCH_REVIEW_COMPLETED, handleBatchReviewCompleted);
    };
  }, [projectId, queryClient]);
}

// CHG-139 REQ-PF-005/006: USER_PROFILE_UPDATED 수신 → 광범위 invalidate + 본인일 때 store 동기화
// - 마운트 시 socket.on, cleanup 시 socket.off
// - payload는 { userId } 1키 — 다른 키는 신뢰하지 않음
// - 본인 외 다른 사용자의 변경에서는 setUser 호출 안 함
export function useUserProfileSocket() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handler = (payload: UserProfileUpdatedPayload) => {
      const targetUserId = payload?.userId;
      if (!targetUserId) return;

      // REQ-PF-005: 광범위 invalidate (마운트된 쿼리만 실제 refetch)
      // 주의: CHG-138 ['user-preference'] 키는 프로필과 무관 — 일부러 제외
      void queryClient.invalidateQueries({ queryKey: ['user-profile-public', targetUserId] });
      void queryClient.invalidateQueries({ queryKey: ['my-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
      void queryClient.invalidateQueries({ queryKey: ['org-members'] });
      void queryClient.invalidateQueries({ queryKey: ['active-members'] });
      void queryClient.invalidateQueries({ queryKey: ['mention-suggestions'] });
      void queryClient.invalidateQueries({ queryKey: ['ideas-list'] });
      void queryClient.invalidateQueries({ queryKey: ['idea-comments'] });

      // REQ-PF-006: 본인일 때만 ['me'] refetch + useAuthStore 동기화
      if (targetUserId === currentUserId) {
        void queryClient.invalidateQueries({ queryKey: ['me'] });
        void (async () => {
          try {
            const fresh = await getMe();
            useAuthStore.getState().setUser(fresh);
          } catch (err) {
            console.warn('[CHG-139] me refetch 실패:', err);
          }
        })();
      }
    };

    socket.on(SOCKET_EVENTS.USER_PROFILE_UPDATED, handler);
    return () => {
      socket.off(SOCKET_EVENTS.USER_PROFILE_UPDATED, handler);
    };
  }, [queryClient, currentUserId]);
}

// 사용자 알림 구독 (헤더에서 사용)
export function useNotificationSocket(onNewNotification: (n: unknown) => void) {
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handler = (data: unknown) => {
      onNewNotification(data);
      const notif = data as { message?: string };
      if (notif?.message) {
        toast(notif.message, { icon: '🔔', duration: 4000 });
      }
    };

    socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, handler);
    return () => {
      socket.off(SOCKET_EVENTS.NOTIFICATION_NEW, handler);
    };
  }, [onNewNotification]);
}
